import { useEffect, useRef, useState } from "react";
import { useGetTemplate } from "~/api/custom-reports/custom-reports";
import { TemplateStatus } from "~/api/model";
import logger from "~/utils/logger";

const TEMPLATE_POLL_INTERVAL_MS = 3000;
const TEMPLATE_READY_TIMEOUT_MS = 180_000;

export type TemplateReadyStatus =
  | "idle"
  | "waiting"
  | "ready"
  | "failed"
  | "timeout";

export const useTemplateReadyStatus = (
  organizationId: string,
  templateId: string | null
): TemplateReadyStatus => {
  const [isTimedOut, setIsTimedOut] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEnabled = !!organizationId && !!templateId;

  const { data } = useGetTemplate(organizationId, templateId ?? "", {
    query: {
      enabled: isEnabled && !isTimedOut,
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        if (
          status === TemplateStatus.completed ||
          status === TemplateStatus.failed
        ) {
          return false;
        }
        return TEMPLATE_POLL_INTERVAL_MS;
      },
      refetchIntervalInBackground: true,
    },
  });

  const isTerminal =
    data?.status === TemplateStatus.completed ||
    data?.status === TemplateStatus.failed;

  useEffect(() => {
    if (!isEnabled || isTerminal || isTimedOut) return;

    timeoutRef.current = setTimeout(() => {
      setIsTimedOut(true);
      logger.error("Template processing timed out", { templateId });
    }, TEMPLATE_READY_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isEnabled, isTerminal, isTimedOut, templateId]);

  if (!isEnabled) return "idle";
  if (isTimedOut) return "timeout";
  if (!data) return "waiting";
  if (data.status === TemplateStatus.completed) return "ready";
  if (data.status === TemplateStatus.failed) return "failed";
  return "waiting";
};
